import { useEffect, useRef } from "react";

// Stylized dotted world map. Continents are approximated with ellipse blobs
// in lon/lat space and rendered as a dot grid on canvas.
const LAND = [
  [-100, 50, 24, 11], [-122, 42, 9, 8], [-78, 38, 9, 7], [-96, 31, 9, 6], [-101, 24, 6, 5],
  [-150, 64, 10, 5], [-42, 73, 8, 4],
  [-62, -8, 10, 9], [-65, -25, 6, 10], [-70, -42, 3.5, 6],
  [10, 50, 11, 5.5], [20, 58, 9, 5], [-3, 40, 4, 3], [22, 42, 5, 3],
  [5, 8, 12, 8], [20, -5, 10, 10], [26, -22, 7, 7], [46, 8, 5, 5], [35, 15, 6, 5],
  [45, 28, 8, 5],
  [55, 55, 22, 9], [95, 58, 22, 9], [130, 52, 12, 7], [85, 38, 13, 7], [105, 33, 11, 7],
  [78, 21, 6, 8],
  [102, 15, 5, 6], [110, -2, 9, 3], [125, -4, 8, 3], [136, -3, 4, 3],
  [139, 37, 3, 5],
  [134, -25, 11, 7],
];

const NODES = [
  { name: "New York", lon: -74, lat: 40.7, c: "#22D3EE", dy: 12 },
  { name: "Toronto", lon: -79.4, lat: 43.7, c: "#22D3EE", dy: -10 },
  { name: "São Paulo", lon: -46.6, lat: -23.5, c: "#7C5CFF" },
  { name: "London", lon: -0.1, lat: 51.5, c: "#22D3EE", dy: -10 },
  { name: "Berlin", lon: 13.4, lat: 52.5, c: "#7C5CFF", dy: 14 },
  { name: "Dubai", lon: 55.3, lat: 25.2, c: "#22D3EE" },
  { name: "Bengaluru", lon: 77.6, lat: 13, c: "#34D399" },
  { name: "Singapore", lon: 103.8, lat: 1.35, c: "#22D3EE" },
  { name: "Tokyo", lon: 139.7, lat: 35.7, c: "#7C5CFF" },
  { name: "Sydney", lon: 151.2, lat: -33.9, c: "#34D399" },
];

const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

export const WorldMapScene = ({ labels = false, converge = true, className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = null;
    let w = 0, h = 0, dpr = 1;
    let landCanvas = null;
    let streams = [];
    let start = performance.now();

    const project = (lon, lat) => [((lon + 180) / 360) * w, ((88 - lat) / 165) * h];

    const inLand = (lon, lat) =>
      LAND.some(([clon, clat, rx, ry]) => {
        const dx = (lon - clon) / rx;
        const dy = (lat - clat) / ry;
        return dx * dx + dy * dy <= 1;
      });

    const buildLand = () => {
      landCanvas = document.createElement("canvas");
      landCanvas.width = w * dpr;
      landCanvas.height = h * dpr;
      const lctx = landCanvas.getContext("2d");
      lctx.scale(dpr, dpr);
      const step = Math.max(5, Math.round(w / 140));
      for (let x = step / 2; x < w; x += step) {
        for (let y = step / 2; y < h; y += step) {
          const lon = (x / w) * 360 - 180;
          const lat = 88 - (y / h) * 165;
          if (inLand(lon, lat)) {
            lctx.fillStyle = "rgba(154,164,178,0.15)";
            lctx.beginPath();
            lctx.arc(x, y, 1.05, 0, Math.PI * 2);
            lctx.fill();
          }
        }
      }
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildLand();
      if (reduced) draw(performance.now());
    };

    const core = () => [w * 0.52, h * 0.46];

    const spawnStream = () => {
      const n = NODES[Math.floor(Math.random() * NODES.length)];
      streams.push({ from: n, t: 0, speed: 0.0016 + Math.random() * 0.0014 });
    };

    const bez = (p0, p1, p2, t) => {
      const a = (1 - t) * (1 - t), b = 2 * (1 - t) * t, c = t * t;
      return [a * p0[0] + b * p1[0] + c * p2[0], a * p0[1] + b * p1[1] + c * p2[1]];
    };

    const draw = (now) => {
      const t = (now - start) / 1000;
      ctx.clearRect(0, 0, w, h);
      if (landCanvas) ctx.drawImage(landCanvas, 0, 0, w, h);

      const [cx, cy] = core();

      if (converge) {
        if (!reduced && streams.length < 6 && Math.random() < 0.03) spawnStream();
        streams.forEach((s) => {
          const [x0, y0] = project(s.from.lon, s.from.lat);
          const mx = (x0 + cx) / 2;
          const my = Math.min(y0, cy) - Math.hypot(cx - x0, cy - y0) * 0.18;
          ctx.strokeStyle = hexA(s.from.c, 0.1);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x0, y0);
          ctx.quadraticCurveTo(mx, my, cx, cy);
          ctx.stroke();
          if (!reduced) s.t += s.speed;
          const [hx, hy] = bez([x0, y0], [mx, my], [cx, cy], Math.min(1, s.t));
          ctx.save();
          ctx.shadowColor = s.from.c;
          ctx.shadowBlur = 8;
          ctx.fillStyle = hexA(s.from.c, 0.9);
          ctx.beginPath();
          ctx.arc(hx, hy, 1.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        });
        streams = streams.filter((s) => s.t < 1);

        // intelligence core: cycles raw -> AI -> verified
        const phase = Math.floor(t / 3) % 3;
        const coreColor = phase === 0 ? "#22D3EE" : phase === 1 ? "#7C5CFF" : "#34D399";
        const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, 46);
        halo.addColorStop(0, hexA(coreColor, 0.22));
        halo.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(cx, cy, 46, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = hexA(coreColor, 0.95);
        ctx.beginPath();
        ctx.arc(cx, cy, 3, 0, Math.PI * 2);
        ctx.fill();
        const ringT = (t % 3) / 3;
        ctx.strokeStyle = hexA(coreColor, 0.4 * (1 - ringT));
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(cx, cy, 6 + ringT * 34, 0, Math.PI * 2);
        ctx.stroke();
      }

      // network nodes
      NODES.forEach((n, i) => {
        const [x, y] = project(n.lon, n.lat);
        const pulse = reduced ? 0.5 : (Math.sin(t * 1.4 + i * 1.7) + 1) / 2;
        ctx.fillStyle = hexA(n.c, 0.85);
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = hexA(n.c, 0.25 + pulse * 0.3);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(x, y, 4 + pulse * 5, 0, Math.PI * 2);
        ctx.stroke();
        if (labels) {
          ctx.font = `500 ${w > 620 ? 10 : 9}px Inter, sans-serif`;
          ctx.fillStyle = "rgba(245,247,250,0.6)";
          const right = x > w * 0.55;
          ctx.textAlign = right ? "right" : "left";
          ctx.fillText(n.name, right ? x - 8 : x + 8, y + 3 + (n.dy || 0));
          ctx.textAlign = "left";
        }
      });

      if (!reduced) raf = requestAnimationFrame(draw);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);
    if (reduced) draw(performance.now());
    else raf = requestAnimationFrame(draw);

    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [labels, converge]);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
};
